import { Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import {
  recipeConfigSource,
  type TrainingSessionProgressDetailView,
  type TrainingSessionRecipeView,
} from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { Fact } from "@/components/dataset";
import { SessionBranchHash } from "./SessionBranchHash";
import { SessionResourceCard } from "./SessionResourceCard";
import { CodeEditor } from "@/components/codeEditor";
import { EmptyState, stateFrameClassName } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { LogViewer } from "@/shared/ui/logViewer/public";
import { formatSessionCreatedAt, trainingProgressStageLabel } from "../trainingSessionFormat";
import { isActiveSessionStatus, sessionSimpleStatus } from "@/modules/training/public";
import { SessionStatusChip } from "@/components/status/SessionStatusChip";
import type { TrainingSessionDetailController } from "../useTrainingSessionDetailController";
import {
  allowSpotLabel,
  decayedSeconds,
  formatEtaSeconds,
  formatMetricNumber,
  formatProgressFraction,
  minLifetimeRequestLabel,
  nodeLifetimeRemainingLabel,
  nodeLifetimeWarningActive,
  requestedGpuLabel,
  sessionConfigLabel,
} from "./trainingSessionDetailFormat";
import { useNowMs } from "./useNowMs";
import styles from "./TrainingSessionPrimaryPanels.module.css";
import {
  maxSessionLogLines,
  useTrainingSessionLogs,
  type TrainingSessionLogsState,
} from "./useTrainingSessionLogs";

type PanelProps = { controller: TrainingSessionDetailController };

export function TrainingSessionLogsPanel({ controller }: PanelProps): ReactNode {
  const { session, rich, richState, isOwner, effectiveStatus } = controller;
  const live = isActiveSessionStatus(effectiveStatus);
  const logs = useTrainingSessionLogs(session.id, live);
  const hasMlflowRun = (rich?.mlflow_run_id ?? null) !== null;
  const submitterLabel = session.registry_user?.display_name ?? (isOwner ? "현재 사용자" : "—");

  return (
    <section className={styles.logs} aria-label="Training 로그">
      <section className={styles.card} aria-label="주요 Training 정보">
        <div className={styles.cardHeader}>
          <Text variant={hatcheryTextModes["caption-bold"]}>주요 Training 정보</Text>
          <SessionStatusChip status={effectiveStatus}>{controller.statusLabel}</SessionStatusChip>
        </div>
        <dl className={styles.primaryFacts}>
          <Fact label="Training 유저" value={submitterLabel} horizontal />
          <Fact
            label="Branch@Hash"
            value={<SessionBranchHash recipe={rich?.recipe} />}
            horizontal
          />
          <Fact label="Config" value={sessionConfigLabel(rich?.recipe, hasMlflowRun)} horizontal />
        </dl>
        {richState.status === "error" ? (
          <EmptyState
            size="sm"
            tone="error"
            action={{ label: "다시 시도", onClick: controller.refresh }}
          >
            제출 상세 정보를 불러오지 못했습니다: {richState.message}
          </EmptyState>
        ) : null}
      </section>

      <div className={styles.secondaryGrid}>
        <ProgressCard controller={controller} />
        <section className={styles.card} aria-label="실행 환경과 시간">
          <div className={styles.cardHeader}>
            <Text variant={hatcheryTextModes["caption-bold"]}>실행 환경과 시간</Text>
          </div>
          <dl className={styles.facts}>
            <Fact label="요청 GPU" value={requestedGpuLabel(rich?.recipe)} />
            <Fact label="스팟 노드" value={allowSpotLabel(rich?.recipe)} />
            {/* 요청 하한("2일 이상" 형식)이다. 관측값은 진행 정보 카드의 "실제 잔여 수명"이 표시한다. */}
            <Fact label="요청 노드 수명" value={minLifetimeRequestLabel(rich?.recipe)} />
            <Fact
              label="생성"
              value={formatSessionCreatedAt(rich?.created_at ?? session.created_at)}
            />
            <Fact label="시작" value={formatSessionCreatedAt(rich?.started_at ?? null)} />
            <Fact label="종료" value={formatSessionCreatedAt(rich?.finished_at ?? null)} />
          </dl>
          <p className={styles.footnote}>GPU 사용량이 아니라 제출 시 요청한 리소스입니다.</p>
        </section>
      </div>

      {/* 순간 지표는 Running pod 에만 있다. 그래서 진행 중인 세션에서만 그리고 폴링한다. */}
      {live ? <SessionResourceCard sessionId={session.id} /> : null}

      <section className={`${styles.card} ${styles.logCard}`} aria-label="Training 로그 본문">
        <div className={styles.cardHeader}>
          <Text variant={hatcheryTextModes["caption-bold"]}>Training 로그</Text>
          <span className={styles.cardMeta}>{sessionLogsMetaLabel(logs)}</span>
        </div>
        {logs.lines.length > 0 ? (
          <LogViewer lines={logs.lines} ariaLabel="Training 로그 출력" />
        ) : (
          <div className={`${stateFrameClassName} ${styles.stateFrame}`}>
            {logs.phase === "error" ? (
              <EmptyState mode="plain" tone="error">
                Training 로그를 불러오지 못했습니다: {logs.lastError ?? "알 수 없는 오류"}
              </EmptyState>
            ) : logs.phase === "loading" ? (
              <LoadingState mode="plain">Training 로그를 불러오고 있습니다…</LoadingState>
            ) : (
              <EmptyState mode="plain">
                {logs.phase === "eof"
                  ? "이 Session에서 확인할 수 있는 로그가 없습니다."
                  : "아직 출력된 로그가 없습니다. Training이 시작되면 여기에 표시됩니다."}
              </EmptyState>
            )}
          </div>
        )}
      </section>
    </section>
  );
}

// 로그 카드 헤더 메타. 폴링 수명주기(로딩/실시간/종료/오류)와 버퍼 포화를 표시한다.
function sessionLogsMetaLabel(logs: TrainingSessionLogsState): string {
  if (logs.phase === "loading") {
    return "불러오는 중…";
  }
  if (logs.phase === "eof") {
    return "로그 스트림 종료";
  }
  if (logs.phase === "error") {
    return "오류";
  }
  if (logs.lastError !== null) {
    return "갱신 실패 · 재시도 중";
  }
  return logs.lines.length >= maxSessionLogLines
    ? `실시간 갱신 중 · 최근 ${maxSessionLogLines.toLocaleString()}줄`
    : "실시간 갱신 중";
}

export function TrainingSessionConfigPanel({ controller }: PanelProps): ReactNode {
  const { rich, configState } = controller;
  const recipe = rich?.recipe;
  const overrideCount = Object.keys(recipe?.config.overrides ?? {}).length;

  return (
    <section className={styles.config} aria-label="Training Config">
      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <Text variant={hatcheryTextModes["caption-bold"]}>제출 Config</Text>
          <span className={styles.cardMeta}>
            {configState.status === "success" && configState.data
              ? configState.data.source
              : "읽기 전용"}
          </span>
        </div>
        <dl className={styles.configFacts}>
          <Fact label="Branch@Hash" value={<SessionBranchHash recipe={recipe} />} />
          <Fact label="Config 이름" value={recipe?.config.name ?? "—"} />
          <Fact label="소스" value={configSourceLabel(recipe?.config)} />
          <Fact label="Overrides" value={overrideCount > 0 ? `${overrideCount}개` : "없음"} />
        </dl>
      </section>

      <section className={`${styles.card} ${styles.configDocument}`}>
        <div className={styles.cardHeader}>
          <Text variant={hatcheryTextModes["caption-bold"]}>Config 전문</Text>
        </div>
        {configState.status === "success" && configState.data !== null ? (
          <div className={styles.editorShell}>
            <CodeEditor
              value={configState.data.body}
              onChange={() => undefined}
              language="yaml"
              readOnly
              rows={24}
              ariaLabel="Training에 사용한 Config 전문"
            />
          </div>
        ) : (
          <div className={`${stateFrameClassName} ${styles.stateFrame}`}>
            {configState.status === "error" ? (
              <EmptyState mode="plain" tone="error">
                {configState.message}
              </EmptyState>
            ) : configState.status === "success" ? (
              <EmptyState mode="plain">저장된 Config 전문이 없습니다.</EmptyState>
            ) : (
              <LoadingState mode="plain">Config 전문을 불러오고 있습니다.</LoadingState>
            )}
          </div>
        )}
      </section>

      {overrideCount > 0 ? (
        <details className={styles.secondaryDetails}>
          <summary>제출 Overrides</summary>
          <pre className={styles.overrideCode}>
            {JSON.stringify(recipe?.config.overrides ?? {}, null, 2)}
          </pre>
        </details>
      ) : null}
    </section>
  );
}

// 감쇄 시계 주기. 남은 시간과 노드 수명 표기가 분 단위라 30초면 충분하다. ETA 가 수명을
// 넘는 순간 배너가 켜지는 지연도 30초 안이다.
const durationTickMs = 30_000;

// eta_seconds(progress)와 node_lifetime.remaining_seconds(rich)는 둘 다 "남은 초" duration 이다.
// 그래서 받은 순간부터 오래된 값이 된다. 각 응답의 수신 시각 기준으로 같은 방식으로 줄인다. 그래서
// 창을 연 채로 폴링이 없어도(progress 는 수동 refresh 만 한다) 표시와 배너 판정이 현재 시각을 따른다.
function ProgressCard({ controller }: PanelProps): ReactNode {
  const { progressState, rich, richReceivedAtMs, progressReceivedAtMs } = controller;
  const lifetime = rich?.node_lifetime;
  const detail = progressState.status === "success" ? progressState.data : null;
  const decayEnabled = lifetime?.state === "measured" || (detail?.eta_seconds ?? null) !== null;
  const nowMs = useNowMs(decayEnabled, durationTickMs);
  const etaSeconds = decayedSeconds(detail?.eta_seconds ?? null, progressReceivedAtMs, nowMs);
  const remainingSeconds =
    lifetime?.state === "measured"
      ? decayedSeconds(lifetime.remaining_seconds, richReceivedAtMs, nowMs)
      : null;
  return (
    <section className={styles.card} aria-label="진행 정보">
      <div className={styles.cardHeader}>
        <Text variant={hatcheryTextModes["caption-bold"]}>진행 정보</Text>
      </div>
      {detail !== null ? (
        <>
          <ProgressFacts
            detail={detail}
            etaSeconds={etaSeconds}
            nodeLifetimeLabel={nodeLifetimeRemainingLabel(lifetime, remainingSeconds)}
          />
          {nodeLifetimeWarningActive(lifetime, etaSeconds, remainingSeconds) ? (
            <p className={styles.lifetimeWarning} role="status">
              노드 잔여 수명이 예상 남은 시간보다 짧습니다. Training이 끝나기 전에 노드가 회수될 수
              있습니다.
            </p>
          ) : null}
        </>
      ) : progressState.status === "error" ? (
        <EmptyState
          size="sm"
          tone="error"
          action={{ label: "다시 시도", onClick: controller.refresh }}
        >
          진행 정보를 불러오지 못했습니다.
        </EmptyState>
      ) : (
        <LoadingState size="sm">진행 정보를 불러오고 있습니다.</LoadingState>
      )}
    </section>
  );
}

function progressPhaseLabel(detail: TrainingSessionProgressDetailView): string {
  if (detail.phase !== null) {
    return trainingProgressStageLabel(detail.phase);
  }
  return detail.status !== null ? sessionSimpleStatus(detail.status).label : "—";
}

function stepAxisLabel(detail: TrainingSessionProgressDetailView): string | null {
  if (detail.completed_steps !== null && detail.target_step !== null) {
    return `${formatMetricNumber(detail.completed_steps)} / ${formatMetricNumber(detail.target_step)}`;
  }
  return detail.current_step !== null ? formatMetricNumber(detail.current_step) : null;
}

function epochAxisLabel(detail: TrainingSessionProgressDetailView): string | null {
  if (detail.completed_epochs !== null && detail.total_epochs !== null) {
    return `${formatMetricNumber(detail.completed_epochs)} / ${formatMetricNumber(detail.total_epochs)}`;
  }
  // step 예산 세션은 총량 없이 현재 epoch 만 온다. 데이터셋을 몇 번 반복했는지는 그 값으로 알 수 있다.
  return detail.current_epoch !== null ? formatMetricNumber(detail.current_epoch) : null;
}

// 학습 예산 단위(step/epoch)에 따라 상류가 한쪽 축만 채우고 반대 축은 null 로 둔다. 예전 세션이
// 쓰는 MLflow 폴백만 두 축을 함께 포함한다. 그래서 칸 수를 고정하지 않고 응답이 정하게 둔다.
function ProgressAxisFacts({ detail }: { detail: TrainingSessionProgressDetailView }): ReactNode {
  const step = stepAxisLabel(detail);
  const epoch = epochAxisLabel(detail);
  if (step === null && epoch === null) {
    return <Fact label="진행" value="—" />;
  }
  return (
    <>
      {step !== null ? <Fact label="STEP" value={step} /> : null}
      {epoch !== null ? <Fact label="EPOCH" value={epoch} /> : null}
    </>
  );
}

// etaSeconds 는 응답 원래 값이 아니라 수신 후 지난 시간을 뺀 값이다(ProgressCard 가 소유한다).
function ProgressFacts({
  detail,
  etaSeconds,
  nodeLifetimeLabel,
}: {
  detail: TrainingSessionProgressDetailView;
  etaSeconds: number | null;
  nodeLifetimeLabel: string;
}): ReactNode {
  return (
    <dl className={styles.facts}>
      <Fact label="단계" value={progressPhaseLabel(detail)} />
      <ProgressAxisFacts detail={detail} />
      <Fact label="진행률" value={formatProgressFraction(detail.progress_fraction)} />
      <Fact label="남은 시간" value={formatEtaSeconds(etaSeconds)} />
      <Fact label="실제 잔여 수명" value={nodeLifetimeLabel} />
    </dl>
  );
}

// 출처를 모르면 "—" 다. 이름으로 repo_config 를 추론하지 않는다(inline 제출도 이름을 포함한다).
// 이름은 바로 위 "Config 이름" 행이 이미 표시하므로 여기서 만들 이유가 없다.
function configSourceLabel(config: TrainingSessionRecipeView["config"] | null | undefined): string {
  return recipeConfigSource(config) ?? "—";
}
